// „Kunde und Objekt berichtigen“ in der Akte (Gernot, 20.10.2026: nach dem
// Senden berichtigt das Büro Name und Adresse; der Verkäufer meldet einen
// Fehler über eine Frage). Je Karte ein „ändern“: Die Felder werden an Ort
// und Stelle bearbeitbar, „Speichern“ schickt sie (PATCH) – geprüft vorher
// mit denselben Regeln wie am Server (Kern: kundeBerichtigenEingabe,
// objektBerichtigenEingabe), eine Meldung steht am Feld. Was der Server
// abweist, steht unter den Feldern.

import { useState, type FormEvent } from "react";
import { type BueroAntwort } from "@happy-energy/kern";
import { aendern, Ikon } from "@happy-energy/oberflaeche";
import { meldungAus } from "../meldung.ts";

/** Was Zod über eine Eingabe sagt – nur, was das Formular braucht (der Kern prüft mit Zod). */
export interface Vertrag<T> {
  safeParse(wert: unknown): { success: true; data: T } | { success: false; error: { issues: readonly { path: readonly PropertyKey[]; message: string }[] } };
}

export interface Feld<K extends string> {
  schluessel: K;
  name: string;
  freiwillig?: boolean;
  typ?: "text" | "tel" | "email";
  autoComplete?: string;
  /** Wie breit im Raster: ganze Zeile (Standard) oder schmal (PLZ). */
  schmal?: boolean;
}

export function Berichtigen<K extends string, T>({
  titel,
  felder,
  anfang,
  vertrag,
  pfad,
  fertig,
  abbrechen,
}: {
  titel: string;
  felder: readonly Feld<K>[];
  anfang: Record<K, string>;
  vertrag: Vertrag<T>;
  /** PATCH an diesen Weg. */
  pfad: string;
  fertig: () => void;
  abbrechen: () => void;
}) {
  const [werte, setWerte] = useState<Record<K, string>>(anfang);
  const [amFeld, setAmFeld] = useState<{ feld: string | null; meldung: string } | null>(null);
  const [vomServer, setVomServer] = useState<string | null>(null);
  const [sendet, setSendet] = useState(false);

  async function speichern(e: FormEvent) {
    e.preventDefault();
    setVomServer(null);
    const gelesen = vertrag.safeParse(werte);
    if (!gelesen.success) {
      const erste = gelesen.error.issues[0];
      const feld = typeof erste?.path[0] === "string" ? erste.path[0] : null;
      setAmFeld({ feld, meldung: erste?.message ?? "Bitte die Felder prüfen." });
      return;
    }
    setAmFeld(null);
    setSendet(true);
    try {
      await aendern<BueroAntwort>(pfad, gelesen.data);
      fertig();
    } catch (f) {
      setVomServer(meldungAus(f));
    } finally {
      setSendet(false);
    }
  }

  return (
    <form
      className="berichtigen"
      onSubmit={(e) => void speichern(e)}
      onKeyDown={(e) => {
        if (e.key === "Escape" && !sendet) abbrechen();
      }}
      aria-label={titel}
    >
      <div className="berichtigen-felder">
        {felder.map((f, i) => (
          <label key={f.schluessel} className={`crm-feld${f.schmal ? " schmal" : ""}`}>
            <span>
              {f.name} {f.freiwillig && <span className="freiwillig">(freiwillig)</span>}
            </span>
            <input
              className="crm-eingabe"
              type={f.typ ?? "text"}
              value={werte[f.schluessel]}
              autoFocus={i === 0}
              autoComplete={f.autoComplete ?? "off"}
              aria-invalid={amFeld?.feld === f.schluessel ? true : undefined}
              onChange={(e) => {
                const wert = e.target.value;
                setWerte((w) => ({ ...w, [f.schluessel]: wert }));
              }}
            />
            {amFeld?.feld === f.schluessel && <span className="crm-fehler-satz">{amFeld.meldung}</span>}
          </label>
        ))}
      </div>
      {amFeld && amFeld.feld === null && <p className="crm-fehler-satz">{amFeld.meldung}</p>}
      {vomServer && (
        <p className="crm-fehler-satz" role="alert">
          {vomServer}
        </p>
      )}
      <div className="crm-knopf-reihe">
        <button type="submit" className="crm-knopf lime klein" disabled={sendet}>
          {sendet ? <span className="kreisel" aria-hidden="true" /> : <Ikon name="haken" groesse="k" />} Speichern
        </button>
        <button type="button" className="crm-knopf rand klein" onClick={abbrechen} disabled={sendet}>
          Abbrechen
        </button>
      </div>
    </form>
  );
}
